import { useState } from "react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { ContactData, SendContactToCRM } from "components/integrations/SendContactToCRMButton";
import { useConnectedParagonCrm } from "lib/hooks/api/integrations/useConnectedParagonCrm";
import { useContactCrmProvider } from "lib/hooks/api/integrations/useFindIntegrationProvider";
import { useSubscription } from "lib/hooks/subscription";
import { ContactSource } from "lib/types/graphQLEnums";
import { EntityActionParams } from "../../../lib/hooks/api/leads/useLeadEntityActions";
import { sysPrimaryDefault } from "../../../lib/themes/colors";
import { OutreachBuilderModal } from "../../outreach_builder/OutreachBuilderModal";
import Button from "../../ui/button/Button";
import EntityCardContent from "../EntityCardContent";
import Property from "../EntityProperty";
import FindEntity from "../FindEntity";
import { SavedEntityCardContainer } from "../utils";

function getEmailValue(contact: ContactData) {
  if (!contact.email) {
    return null;
  }

  return <EmailLink href={`mailto:${contact.email}`}>{contact.email}</EmailLink>;
}

function getPhoneValue(contact: ContactData) {
  if (!contact.phone) {
    return null;
  }

  return <PhoneNumber>{contact.phone}</PhoneNumber>;
}

export function SaveContactEntitiesCard({
  contacts,
  entityUrl,
  showAddEntity,
  removeEntity,
}: {
  contacts: ContactData[];
  entityUrl: string;
  removeEntity: (entity: EntityActionParams) => void;
  showAddEntity?: boolean;
}) {
  const [selectedContact, setSelectedContact] = useState<ContactData | null>(null);
  return (
    <Flex column gap={16}>
      {contacts.map((contact) => (
        <ContactCard
          key={contact.id}
          contact={contact}
          onOutreachClick={(c) => setSelectedContact(c)}
          removeEntity={() =>
            removeEntity({
              // A Cognism contact is a `contact_cognism` entity; its `id` is the stored
              // entityId (the cognism_redeemed_contact row), so removal still keys on id.
              entityType: contact.source === ContactSource.Cognism ? "contact_cognism" : "contact",
              entityId: contact.id,
            })
          }
        />
      ))}
      <FindEntity
        entity="contact"
        url={entityUrl}
        isEmptyState={!contacts.length}
        showAddEntity={showAddEntity}
      />
      <OutreachBuilderModal
        isOpen={!!selectedContact}
        onClose={() => setSelectedContact(null)}
        buyerName={selectedContact?.buyer?.name ?? ""}
        contactEmail={selectedContact?.email ?? ""}
        contactName={`${selectedContact?.firstName} ${selectedContact?.lastName}`}
        jobTitle={selectedContact?.jobTitle ?? ""}
      />
    </Flex>
  );
}

function ContactCard({
  contact,
  onOutreachClick,
  removeEntity,
}: {
  contact: ContactData;
  onOutreachClick: (c: ContactData) => void;
  removeEntity: () => void;
}) {
  const { data: providerTypeResponse } = useContactCrmProvider();
  const providerType = providerTypeResponse?.providerType;
  // A CRM reached through Paragon is not reported above — its push comes from the company's own
  // field mapping rather than from the fields we name — so it is asked for separately. Without it
  // this card offers no way to send a contact to a CRM the company has definitely connected.
  const paragonCrm = useConnectedParagonCrm();

  const subscription = useSubscription();
  const hasAccess = subscription.hasDataTypes("CONTACTS");

  return (
    <Container aria-label="Contact card">
      <EntityCardContent
        header={<Text fontWeight="600">{`${contact.firstName} ${contact.lastName}`}</Text>}
        entity="contact"
        removeEntity={removeEntity}
        cta={
          hasAccess && (providerType || paragonCrm) ? (
            <SendContactToCRM
              key="send"
              buttonType="secondary"
              contact={contact}
              fullWidth={true}
              justifyContent="left"
              showingTour={false}
            />
          ) : null
        }
      >
        <Flex gap={16} width="100%">
          <Flex flexDirection="column" gap={16} justifyContent="space-between" width="100%">
            <Flex column gap={16}>
              <Property title="Role" value={contact.jobTitle} icon="user01" />
              <Property title="Email" value={getEmailValue(contact)} icon="mail01" />
              <Property title="Phone" value={getPhoneValue(contact)} icon="phone" />
            </Flex>
            {hasAccess && (
              <Button
                buttonType="secondary"
                icon="send"
                iconProps={{ color: sysPrimaryDefault }}
                onClick={() => onOutreachClick(contact)}
                fullWidth
              >
                Generate outreach
              </Button>
            )}
          </Flex>
        </Flex>
      </EntityCardContent>
    </Container>
  );
}

const EmailLink = styled.a(({ theme }) => ({
  color: theme.colors.sysPrimaryDefault,
  // An email address has no break opportunity, so as a flex item its automatic minimum is the
  // whole string; `anywhere` lowers that min-content contribution so the link wraps in its column.
  overflowWrap: "anywhere",
  minWidth: 0,
}));

const PhoneNumber = styled.span(({ theme }) => ({
  color: theme.colors.sysPrimaryDefault,
}));

const Container = styled(SavedEntityCardContainer)({
  cursor: "default",
  flexDirection: "column",
  alignSelf: "stretch",
  gap: "16px",
  padding: "24px",
  "&:hover": undefined,
});
